import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { MapPin, ShieldCheck, Truck, Lock, Flame, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";
import { Stars } from "@/pages/Ranchers";
import { ProductCard } from "@/pages/Marketplace";

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [qty, setQty] = useState(1);
  const [msg, setMsg] = useState("");
  const [img, setImg] = useState(0);

  useEffect(() => { api.get(`/marketplace/products/${id}`).then(r => { setP(r.data); setImg(0); }).catch(() => {}); }, [id]);
  if (!p) return <div className="max-w-7xl mx-auto px-6 py-20" data-testid="product-loading">Loading…</div>;

  const gallery = [p.image_url, ...(p.images || [])].filter(Boolean);
  const r = p.rancher || {};
  const locked = p.members_only && !user?.is_member;

  const addToCart = async () => {
    setMsg("");
    if (!user) { nav("/login?next=/product/" + id); return; }
    try { await api.post("/cart/add", { product_id: p.id, quantity: qty }); setMsg("Added to cart — shipped directly by " + p.vendor_name + "."); }
    catch (e) { setMsg(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="product-detail">
      <Seo title={`${p.name} — ${p.vendor_name}`} description={(p.description || "").slice(0, 160)} path={`/product/${p.id}`}/>
      <div className="max-w-7xl mx-auto px-6 pt-6 text-xs text-[var(--ff-text-muted)] flex items-center gap-1" data-testid="product-breadcrumb">
        <Link to="/marketplace" className="hover:underline">Marketplace</Link><ChevronRight size={12}/>
        <Link to={`/marketplace?category=${p.category}`} className="hover:underline capitalize">{p.category}</Link><ChevronRight size={12}/>
        <span>{p.name}</span>
      </div>
      <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-2 gap-12">
        <div>
          <div className="rounded-2xl overflow-hidden bg-[var(--ff-muted)] aspect-square relative">
            {gallery[img] && <img src={gallery[img]} alt={p.name} className="w-full h-full object-cover"/>}
            <div className="absolute top-4 left-4 flex gap-2">
              {p.members_only && <span className="inline-flex items-center gap-1 bg-[var(--ff-primary)] text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"><Lock size={9}/> Members only</span>}
              {p.limited_release && <span className="inline-flex items-center gap-1 bg-[var(--ff-citrus)] text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full"><Flame size={9}/> Limited release</span>}
            </div>
          </div>
          {gallery.length > 1 && <div className="mt-3 flex gap-2">{gallery.map((g, i) => <button key={i} onClick={() => setImg(i)} className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${img === i ? "border-[var(--ff-primary)]" : "border-transparent"}`}><img src={g} alt="" className="w-full h-full object-cover"/></button>)}</div>}
        </div>
        <div>
          <Link to={`/ranchers/${p.vendor_id}`} className="inline-flex items-center gap-2 group" data-testid="product-vendor-link">
            {r.logo_url && <img src={r.logo_url} alt="" className="w-8 h-8 rounded-full object-cover"/>}
            <span className="overline group-hover:underline">{p.vendor_name}</span>
            {r.location && <span className="text-xs text-[var(--ff-text-muted)] flex items-center gap-1"><MapPin size={10}/> {r.location}</span>}
          </Link>
          <h1 className="font-display text-4xl sm:text-5xl mt-3 leading-tight">{p.name}</h1>
          <div className="mt-3 flex items-center gap-4"><Stars avg={p.rating.avg} count={p.rating.count}/><span className="text-sm text-[var(--ff-text-muted)]">{p.unit}{p.state ? ` · Raised in ${p.state}` : ""}</span></div>
          <div className="mt-4 flex flex-wrap gap-2">{(p.practices || []).map(x => <span key={x} className="text-xs uppercase tracking-wider px-3 py-1 bg-[var(--ff-accent-kraft)]/40 text-[var(--ff-primary)] rounded-full">{x}</span>)}</div>
          <p className="mt-6 text-[var(--ff-text-secondary)] leading-relaxed">{p.description}</p>

          <div className="mt-8 flex items-baseline gap-3">
            <span className="font-display text-5xl text-[var(--ff-primary)]" data-testid="product-price">${Number(p.price).toFixed(2)}</span>
            <span className="text-[var(--ff-text-muted)]">/ {p.unit}</span>
            {!user?.is_member && !p.members_only && <span className="text-xs text-[var(--ff-text-muted)]">Members pay this price · non-members +15%</span>}
          </div>

          <div className="mt-6 flex items-center gap-4 flex-wrap">
            <div className="flex items-center border border-[var(--ff-border)] rounded-full">
              <button onClick={() => setQty(q => Math.max(1, q - 1))} className="px-4 py-2" data-testid="qty-minus">−</button>
              <span className="px-3 min-w-[2rem] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(q => q + 1)} className="px-4 py-2" data-testid="qty-plus">+</button>
            </div>
            {locked ? <Link to="/pricing" className="btn-primary" data-testid="members-only-cta"><Lock size={14}/> Join to unlock</Link>
              : <button onClick={addToCart} disabled={!p.in_stock} className="btn-primary disabled:opacity-50" data-testid="add-to-cart-btn">{p.in_stock ? "Add to cart" : "Sold out"}</button>}
            <span className="text-xs text-[var(--ff-text-muted)]">{p.in_stock ? `${p.stock} in stock` : ""}</span>
          </div>
          {msg && <p className="mt-4 text-sm text-[var(--ff-primary)]" data-testid="add-to-cart-msg">{msg}</p>}

          <div className="mt-8 card-flat p-5 space-y-3 text-sm" data-testid="fulfilled-by-card">
            <p className="flex items-center gap-2"><ShieldCheck size={16} className="text-[var(--ff-primary)]"/> <strong>Sold and shipped by {p.vendor_name}</strong> — an independent, Farm To You–approved rancher.</p>
            <p className="flex items-start gap-2 text-[var(--ff-text-secondary)]"><Truck size={16} className="text-[var(--ff-primary)] shrink-0 mt-0.5"/> {r.shipping_info || "Packed and shipped cold-chain directly from the ranch. Transit times vary by destination."}</p>
            <Link to={`/ranchers/${p.vendor_id}`} className="inline-flex items-center gap-1 text-[var(--ff-primary)] underline">Visit the {p.vendor_name} storefront <ChevronRight size={14}/></Link>
          </div>
          {!user?.is_member && !locked && (
            <div className="mt-4 text-sm text-[var(--ff-text-secondary)] bg-[var(--ff-muted)] rounded-lg p-4" data-testid="membership-notice">
              Members get preferred pricing, free shipping, and early drop access. <Link to="/pricing" className="underline text-[var(--ff-primary)]">See plans →</Link>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-16 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-7" data-testid="product-reviews">
          <h2 className="font-display text-3xl">Reviews</h2>
          {p.reviews.length === 0 && <p className="mt-3 text-sm text-[var(--ff-text-muted)]">No reviews yet. Verified buyers can leave one after delivery.</p>}
          <div className="mt-4 space-y-3">{p.reviews.map(rv => (
            <div key={rv.id} className="card-flat p-4">
              <div className="flex justify-between"><Stars avg={rv.rating} count={1}/><span className="text-xs text-[var(--ff-text-muted)]">{(rv.created_at || "").slice(0, 10)}</span></div>
              {rv.title && <p className="font-medium mt-1">{rv.title}</p>}<p className="text-sm mt-1">{rv.body}</p>
              <p className="text-xs mt-2 text-[var(--ff-text-muted)]">{rv.author} · <span className="text-[var(--ff-primary)]">✓ Verified purchase</span></p>
              {rv.response && <div className="mt-2 bg-[var(--ff-muted)] rounded p-3 text-sm"><p className="overline">{rv.response.by} responded</p><p className="mt-1">{rv.response.body}</p></div>}
            </div>))}</div>
        </div>
        <div className="md:col-span-5">
          <h2 className="font-display text-3xl">More from {p.vendor_name}</h2>
          <div className="mt-4 grid grid-cols-2 gap-4" data-testid="product-related">{p.related.map(x => <ProductCard key={x.id} p={x}/>)}</div>
        </div>
      </div>
    </div>
  );
}
